import React, { useState, useEffect } from 'react';
import {
  Calendar, LogOut, Menu, X, ChevronDown, Building2
} from 'lucide-react';
import { useLanguage } from './components/LanguageContext';
import LanguageToggle from './components/LanguageToggle';
import { isAuthenticated, getCurrentUser, logout, fetchWithAuth } from './utils/api';
import { NAV_ITEMS } from './navItems';
import useIsMobile from './hooks/useIsMobile';
import MobileHeader from './components/MobileHeader';
import NavDrawer from './components/NavDrawer';
import BottomNav from './components/BottomNav';

// Auth Components
import Login from './components/Login';
import Signup from './components/Signup';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

// Main Components
import Dashboard from './components/Dashboard';
import WaitingList from './components/WaitingList';
import MyCalendar from './components/MyCalendar';
import DayOffManagement from './components/DayOffManagement';
import WorkingBranches from './components/WorkingBranches';
import Profile from './components/Profile';

function App() {
  const { print_lang, isLoading: langLoading } = useLanguage();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Get initial page from URL
  useEffect(() => {
    const path = window.location.pathname.replace('/', '') || 'dashboard';
    const validPages = ['login', 'signup', 'verify-email', 'forgot-password', 'reset-password', 'dashboard', 'waiting-list', 'calendar', 'dayoff', 'branches', 'profile'];
    if (validPages.includes(path)) {
      setCurrentPage(path);
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      if (isAuthenticated()) {
        const userData = getCurrentUser();
        setUser(userData);
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
        setUser(null);
        // Redirect to login if on protected page
        const publicPages = ['login', 'signup', 'verify-email', 'forgot-password', 'reset-password'];
        if (!publicPages.includes(currentPage)) {
          setCurrentPage('login');
        }
      }
    };
    checkAuth();
  }, [currentPage]);

  // Update URL when page changes
  useEffect(() => {
    const path = currentPage === 'dashboard' ? '/' : `/${currentPage}`;
    window.history.pushState({}, '', path);
  }, [currentPage]);

  // Handle login success
  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
    setCurrentPage('dashboard');
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setUser(null);
    setCurrentPage('login');
  };

  // Handle branch switch
  const handleBranchSwitch = async (branchId) => {
    try {
      // console.log('=== Switching branch to:', branchId);
      const response = await fetchWithAuth('/api/dentists/switch-branch', {
        method: 'POST',
        body: JSON.stringify({ branch_id: branchId })
      });
      // console.log('Switch branch response:', response);
      if (response.success) {
        localStorage.setItem('dentist_token', response.token);
        const updatedUser = { ...user, branch_id: response.branch.branch_id, admin_id: response.branch.admin_id };
        // console.log('Updated user after branch switch:', updatedUser);
        localStorage.setItem('dentist_user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setShowBranchDropdown(false);
        // Force page refresh to reload data with new branch
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to switch branch:', error);
      alert('Failed to switch branch: ' + error.message);
    }
  };

  // Render page content
  const renderPage = () => {
    // Public pages (no auth required)
    if (currentPage === 'login') return <Login onSuccess={handleLoginSuccess} onNavigate={setCurrentPage} />;
    if (currentPage === 'signup') return <Signup onNavigate={setCurrentPage} />;
    if (currentPage === 'verify-email') return <VerifyEmail onNavigate={setCurrentPage} />;
    if (currentPage === 'forgot-password') return <ForgotPassword onNavigate={setCurrentPage} />;
    if (currentPage === 'reset-password') return <ResetPassword onNavigate={setCurrentPage} />;

    // Protected pages (require auth)
    if (!isLoggedIn) return <Login onSuccess={handleLoginSuccess} onNavigate={setCurrentPage} />;

    switch (currentPage) {
      case 'dashboard': return <Dashboard />;
      case 'waiting-list': return <WaitingList />;
      case 'calendar': return <MyCalendar />;
      case 'dayoff': return <DayOffManagement />;
      case 'branches': return <WorkingBranches />;
      case 'profile': return <Profile />;
      default: return <Dashboard />;
    }
  };

  // Show loading while translations load
  if (langLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="spinner"></div>
      </div>
    );
  }

  // Public pages layout (no sidebar)
  const publicPages = ['login', 'signup', 'verify-email', 'forgot-password', 'reset-password'];
  if (publicPages.includes(currentPage) || !isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        {renderPage()}
      </div>
    );
  }

  // Main app layout with sidebar
  const currentNavItem = NAV_ITEMS.find((item) => item.id === currentPage);
  const pageTitle = currentNavItem
    ? print_lang(currentNavItem.labelKey, currentNavItem.labelFallback)
    : print_lang('dashboard', 'Dashboard');

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {!isMobile && (
      <aside className={`sidebar-desktop ${sidebarOpen ? 'w-64' : 'w-20'} bg-blue-900 text-white transition-all duration-300 flex flex-col`}>
        {/* Logo/Header */}
        <div className="p-4 flex items-center justify-between border-b border-blue-800">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Calendar className="w-8 h-8" />
              <span className="font-bold text-lg">{print_lang('dentist_portal', 'Dentist Portal')}</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-blue-800 transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Branch Selector - Always show current branch */}
        {user?.branches && user.branches.length >= 1 && (
          <div className="p-4 border-b border-blue-800">
            <div className="relative">
              <button
                onClick={() => user.branches.length > 1 && setShowBranchDropdown(!showBranchDropdown)}
                className={`w-full flex items-center justify-between px-3 py-2 bg-blue-800 rounded-lg transition-colors ${
                  user.branches.length > 1 ? 'hover:bg-blue-700 cursor-pointer' : 'cursor-default'
                }`}
              >
                <span className="truncate text-sm">
                  {user.branches.find(b => b.branch_id === user.branch_id)?.branch_name || `Branch ${user.branch_id}`}
                </span>
                {user.branches.length > 1 && (
                  <ChevronDown className={`w-4 h-4 transition-transform ${showBranchDropdown ? 'rotate-180' : ''}`} />
                )}
              </button>
              {showBranchDropdown && user.branches.length > 1 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg overflow-hidden z-10">
                  {user.branches.map(branch => (
                    <button
                      key={branch.branch_id}
                      onClick={() => handleBranchSwitch(branch.branch_id)}
                      className={`w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 transition-colors
                        ${branch.branch_id === user.branch_id ? 'bg-blue-100 font-medium' : ''}`}
                    >
                      {branch.branch_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {user.branches.length === 1 && (
              <p className="text-xs text-blue-300 mt-1 text-center">
                {print_lang('only_one_branch', 'Only assigned to this branch')}
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => setCurrentPage(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                ${currentPage === item.id
                  ? 'bg-blue-700 text-white'
                  : 'text-blue-200 hover:bg-blue-800 hover:text-white'
                }`}
            >
              <item.Icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{print_lang(item.labelKey, item.labelFallback)}</span>}
            </button>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="p-4 border-t border-blue-800 space-y-2">
          {sidebarOpen && <LanguageToggle />}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-red-300 hover:bg-red-900/50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>{print_lang('logout', 'Logout')}</span>}
          </button>
        </div>
      </aside>
      )}

      {isMobile && (
        <>
          <MobileHeader
            title={pageTitle}
            onOpenDrawer={() => setDrawerOpen(true)}
            user={user}
          />
          <NavDrawer
            open={drawerOpen}
            user={user}
            currentPage={currentPage}
            onNavigate={setCurrentPage}
            onLogout={handleLogout}
            onClose={() => setDrawerOpen(false)}
            onBranchSwitch={handleBranchSwitch}
          />
        </>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header - desktop only. MobileHeader (rendered above when
            isMobile) already shows this same page title in the fixed bar;
            keeping this one too would stack two headers under it on a phone. */}
        <header className="hidden md:flex bg-white shadow-sm px-6 py-4 items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">{pageTitle}</h1>
            {user && (
              <p className="text-sm text-gray-500">
                {print_lang('welcome', 'Welcome')}, {user.name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            {user?.branches?.find(b => b.branch_id === user.branch_id) && (
              <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                <Building2 className="w-4 h-4 inline mr-1" />
                {user.branches.find(b => b.branch_id === user.branch_id)?.branch_name}
              </span>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto p-6">
          {renderPage()}
        </div>
      </main>

      {isMobile && (
        <BottomNav
          currentPage={currentPage}
          onNavigate={setCurrentPage}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}

export default App;
